import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { Input, Alert } from 'reactstrap';

import {
  colors,
  smallCapsStyle,
  inputStyle,
  headingStyle,
  maskSecret,
  lastChecked,
  connectionProblem,
  StepCard,
  StepWithAside,
  KeepConnectedBox,
  CopyField,
  FieldRow,
  Field,
  ResultList,
  ConnectedSummary,
  PrimaryButton,
  OutlineButton,
} from './ConnectionParts';

// Shopify, new connection (route A): the business creates its own app in Shopify's Dev Dashboard and gives Steppr
// the shop address, Client ID and Client secret. "Test and connect" calls shopifyConnectApp, which saves only when
// every check passes. Used on the Website Connection page (main shop) and in the linked-shop form (linked shops).

export const SHOPIFY_APP_NAME = 'Steppr';
export const SHOPIFY_APP_URL = 'https://shopify.dev/apps/default-app-home';
export const SHOPIFY_SCOPES = 'read_price_rules,write_price_rules,read_discounts,write_discounts';

// Same wording as the server (functions/company/shopifyConnectApp.js), used when a reply carries no message
export const SHOPIFY_MESSAGES = {
  shopNotFound: 'Check the shop address ends in .myshopify.com (Shopify admin: Settings > Domains).',
  wrongClientId: "The Client ID doesn't match an app. Copy it again from the app's Settings in Dev Dashboard.",
  wrongSecret: "The Client secret is wrong or has been changed. Copy it again from the app's Settings in Dev Dashboard.",
  notInstalled: 'Install the app on your store: Dev Dashboard > Apps > the \u22EE menu on the app > Install app.',
  otherOrganisation: "Create the app from your own store's admin (Settings > Apps > Develop apps), logged in as the store owner.",
  missingScopes: 'Your app is missing some permissions. Add them in a new app version, release it, then try again.',
  shopClosed: 'Shopify says this store is closed or paused. Reopen it in Shopify, then try again.',
  temporary: "Shopify didn't answer just now. Please try again in a minute.",
  refused: "Shopify refused the connection. Check the Client ID and Client secret, and that the app is released and installed on this store, then try again.",
  notAllowed: "You don't have permission to change this business's connection.",
  missingData: 'Please fill in the shop address, Client ID and Client secret.',
};

// A new-route connection: the business's own app (mode 'devDashboard') with a Client ID; no stored token
export const isNewRouteAccess = (access) => !!(access && access.mode === 'devDashboard' && access.clientId);

// Under today's form for a business on the legacy (stored token) connection: approved wording (4 Nov 2026)
export const LegacyShopifyNotice = ({ onSetUpNew, disabled }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px 20px', background: colors.page, borderRadius: 12, padding: '16px 20px', marginTop: 20, color: colors.text }}>
    <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={colors.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 8h.01" />
    </svg>
    <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5, flex: '1 1 420px', minWidth: 0 }}>
      Your Shopify connection works and will continue to work, so no action is needed. Shopify now requires a different type of app for new connections, so if you ever need to reconnect, you can set up a new connection here. Your existing vouchers keep working either way.
    </p>
    <OutlineButton onClick={onSetUpNew} disabled={disabled}>Set up a new connection</OutlineButton>
  </div>
);

LegacyShopifyNotice.propTypes = {
  onSetUpNew: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

LegacyShopifyNotice.defaultProps = {
  disabled: false,
};

const KEEP_CONNECTED = [
  'Delete or uninstall the Steppr app',
  'Rotate the client secret',
  'Turn on an IP allowlist for the app',
];

// The reply's checks, in the order the server runs them, as result lines. Lines stop at the first failed check,
// which carries the plain fix.
export const shopifyResultLines = (res) => {
  if (!res) return [];
  const checks = res.checks || {};
  const fix = res.message || SHOPIFY_MESSAGES[res.reason] || SHOPIFY_MESSAGES.temporary;
  const missing = (res.missing && res.missing.length) ? res.missing : null;
  const steps = [
    { key: 'shopFound', ok: checks.shopFound, text: 'Store found' },
    { key: 'appAccepted', ok: checks.appAccepted, text: 'App installed and details correct' },
    {
      key: 'permissions',
      ok: checks.permissions,
      text: 'All 4 permissions granted',
      failText: missing ? `Missing permissions: ${missing.join(', ')}. Add them in a new app version, release it and approve it.` : null,
    },
    { key: 'readable', ok: checks.shopRead && checks.discountRead, text: 'Store and discounts can be read' },
  ];

  if (res.status === 'success') {
    return steps.map((s) => ({ key: s.key, ok: true, text: s.text }));
  }

  // Refused before any check ran (no permission, missing data): only the message
  if (!res.checks) return [{ key: 'error', ok: false, text: fix }];

  const lines = [];
  for (let i = 0; i < steps.length; i += 1) {
    const s = steps[i];
    if (s.ok) {
      lines.push({ key: s.key, ok: true, text: s.text });
    } else {
      lines.push({ key: s.key, ok: false, text: s.failText || fix });
      return lines;
    }
  }
  lines.push({ key: 'error', ok: false, text: fix });
  return lines;
};

class ShopifyNewConnection extends Component {
  static propTypes = {
    // connect({ shopName, clientId, clientSecret }) -> Promise of the shopifyConnectApp reply (never rejects)
    connect: PropTypes.func.isRequired,
    // called with (reply, secretLast4) after a successful connection
    onConnected: PropTypes.func,
    // the saved new-route connection (accessData / apiData with mode 'devDashboard'), or null
    existing: PropTypes.shape(),
    secretLast4: PropTypes.string,
    checkedAt: PropTypes.number,
    disabled: PropTypes.bool,
    // shown under the summary / steps: e.g. "Back to your current connection" for a legacy business
    onCancel: PropTypes.func,
    cancelText: PropTypes.string,
    // linked-shop form: no Connected summary, smaller cards
    compact: PropTypes.bool,
    // linked-shop form: checks run before the call (e.g. business name and email filled)
    validate: PropTypes.func,
  };

  static defaultProps = {
    onConnected: null,
    existing: null,
    secretLast4: null,
    checkedAt: null,
    disabled: false,
    onCancel: null,
    cancelText: 'Cancel',
    compact: false,
    validate: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      editing: !(props.existing && props.existing.clientId),
      shopName: '',
      clientId: '',
      clientSecret: '',
      busy: false,
      result: null,
      formError: null,
    };
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentDidUpdate(prevProps) {
    const { existing } = this.props;
    const { busy } = this.state;
    const had = !!(prevProps.existing && prevProps.existing.clientId);
    const has = !!(existing && existing.clientId);

    // The saved connection loaded after the first render: show its summary
    if (!had && has && !busy) {
      this.setState({ editing: false });
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  handleChange = (e) => {
    if (e && e.target && e.target.name) {
      const { name, value } = e.target;
      this.setState({ [name]: value, formError: null });
    }
  }

  startUpdate = () => {
    this.setState({ editing: true, shopName: '', clientId: '', clientSecret: '', result: null, formError: null });
  }

  stopUpdate = () => {
    this.setState({ editing: false, shopName: '', clientId: '', clientSecret: '', result: null, formError: null });
  }

  testAndConnect = () => {
    const { connect, onConnected, validate } = this.props;
    const { shopName, clientId, clientSecret, busy } = this.state;

    if (busy) return null;

    const shop = String(shopName || '').trim();
    const id = String(clientId || '').trim();
    const secret = String(clientSecret || '').trim();

    if (validate) {
      const problem = validate();
      if (problem) {
        this.setState({ formError: problem, result: null });
        return null;
      }
    }

    if (!shop || !id || !secret) {
      this.setState({ formError: SHOPIFY_MESSAGES.missingData, result: null });
      return null;
    }

    this.setState({ busy: true, result: null, formError: null });

    return Promise.resolve(connect({ shopName: shop, clientId: id, clientSecret: secret })).then((res) => {
      const reply = res || { status: 'error', reason: 'temporary' };
      if (!this.mounted) return;

      if (reply.status === 'success') {
        const last4 = secret.slice(-4);
        this.setState({ busy: false, result: reply, editing: false, shopName: '', clientId: '', clientSecret: '' });
        if (onConnected) onConnected(reply, last4);
      } else {
        this.setState({ busy: false, result: reply });
      }
    }).catch(() => {
      if (this.mounted) this.setState({ busy: false, result: { status: 'error', reason: 'temporary' } });
    });
  }

  renderSteps() {
    const { disabled, existing, onCancel, cancelText, compact } = this.props;
    const { shopName, clientId, clientSecret, busy, result, formError } = this.state;
    const hasExisting = !!(existing && existing.clientId);
    const lines = shopifyResultLines(result);

    return (
      <>
        <StepCard number={1}>
          <StepWithAside aside={<KeepConnectedBox items={KEEP_CONNECTED} />}>
            <h2 style={headingStyle}>Create the Steppr app in Shopify</h2>
            <ol style={{ margin: 0, paddingLeft: 20, fontSize: 16, lineHeight: 1.7 }}>
              <li>Log in to your Shopify admin as the store owner. Go to Settings, then Apps, then Develop apps, and press Build apps in Dev Dashboard.</li>
              <li>Press Create app. Under Start from Dev Dashboard, enter the app name Steppr and press Create.</li>
              <li>On the version screen, enter the App URL below, set Webhooks API Version to the latest, and enter the permissions (scopes) below.</li>
              <li>Press Create app, then press Release.</li>
              <li>Install the app: in Dev Dashboard's Apps list, press the {'\u22EE'} menu at the right of the app's row, then Install app. Choose your store and press Install.</li>
              <li>In Dev Dashboard, press the app's name, then Settings, and copy the Client ID and Client secret into step 2.</li>
            </ol>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <CopyField label="App name" value={SHOPIFY_APP_NAME} />
              <CopyField label="App URL" value={SHOPIFY_APP_URL} />
              <CopyField label="Permissions" value={SHOPIFY_SCOPES} />
            </div>
            <p style={{ margin: 0, fontSize: 14, color: colors.muted }}>
              If you open the Steppr app inside your Shopify admin, you'll see a placeholder page ("This domain is for use in documentation examples..."). That's normal: the app has no screen of its own.
            </p>
          </StepWithAside>
        </StepCard>

        <StepCard number={2}>
          <h2 style={headingStyle}>Enter the app's details</h2>
          <p style={{ margin: 0, fontSize: 15, color: colors.muted }}>Find the Client ID and Client secret in the app's Settings in the Dev Dashboard.</p>
          <FieldRow>
            <Field id={compact ? 'linkedShopifyShopName' : 'shopifyAppShopName'} label="Shop address">
              <Input
                name="shopName"
                id={compact ? 'linkedShopifyShopName' : 'shopifyAppShopName'}
                type="text"
                placeholder="yourstore.myshopify.com"
                autoComplete="off"
                value={shopName}
                style={inputStyle}
                onChange={this.handleChange}
              />
            </Field>
            <Field id={compact ? 'linkedShopifyClientId' : 'shopifyAppClientId'} label="Client ID">
              <Input
                name="clientId"
                id={compact ? 'linkedShopifyClientId' : 'shopifyAppClientId'}
                type="text"
                autoComplete="off"
                value={clientId}
                style={inputStyle}
                onChange={this.handleChange}
              />
            </Field>
            <Field id={compact ? 'linkedShopifyClientSecret' : 'shopifyAppClientSecret'} label="Client secret">
              <Input
                name="clientSecret"
                id={compact ? 'linkedShopifyClientSecret' : 'shopifyAppClientSecret'}
                type="password"
                autoComplete="new-password"
                value={clientSecret}
                style={inputStyle}
                onChange={this.handleChange}
              />
            </Field>
          </FieldRow>
        </StepCard>

        <StepCard number={3}>
          <h2 style={headingStyle}>Test and connect</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
            <PrimaryButton onClick={this.testAndConnect} disabled={disabled} busy={busy}>
              {busy ? 'Testing' : 'Test and connect'}
            </PrimaryButton>
            <span style={{ fontSize: 14, color: colors.muted }}>Nothing is saved until the test passes.</span>
            {(hasExisting || onCancel) ? (
              <OutlineButton onClick={hasExisting ? this.stopUpdate : onCancel} disabled={busy} style={{ marginLeft: 'auto' }}>
                {hasExisting ? 'Cancel' : cancelText}
              </OutlineButton>
            ) : null}
          </div>
          {hasExisting ? (
            <p style={{ margin: 0, fontSize: 14, color: colors.muted }}>Your current connection stays active until the new details pass the test.</p>
          ) : null}
          {formError ? (<Alert color="danger" style={{ margin: 0 }}>{formError}</Alert>) : null}
          <ResultList lines={lines} />
        </StepCard>
      </>
    );
  }

  render() {
    const { existing, secretLast4, checkedAt, disabled, compact, status } = this.props;
    const { editing, result } = this.state;
    const hasExisting = !!(existing && existing.clientId);

    if (editing || !hasExisting) {
      return this.renderSteps();
    }

    if (compact) return null;

    // "Last checked" only for a test made in this visit; otherwise when it was connected
    const checked = lastChecked(checkedAt);
    const connected = lastChecked(existing.connectedAt);
    // A test that passed in this visit is newer than the stored status
    const problem = checkedAt ? null : connectionProblem(status);

    return (
      <>
        {(result && result.status === 'success') ? (
          <div style={{ marginBottom: 20 }}>
            <ResultList lines={shopifyResultLines(result)} />
          </div>
        ) : null}
        <p style={smallCapsStyle}>Your Shopify connection</p>
        <ConnectedSummary
          address={existing.shopName || existing.shopifyAPIShopName}
          secretText={secretLast4 ? `Client secret ${maskSecret(secretLast4)}` : 'Client secret saved'}
          checkedText={checked ? `Last checked ${checked}` : (connected ? `Connected ${connected}` : null)}
          problem={problem}
          problemHelp="Your campaigns may be paused until this is fixed. Check the Steppr app is still installed on your store (Dev Dashboard > Apps), or use Update details with the app's current Client ID and secret."
          buttonText="Update details"
          onButton={this.startUpdate}
          buttonDisabled={disabled}
        />
      </>
    );
  }
}

export default ShopifyNewConnection;
